import undeadUnitsJson from "../content/undead-units.json";
import workshopOverrideJson from "../content/skill-overrides.json";
import { isAllowedAttackIntervalMs } from "./attack-interval";
import { isAllowedSkillRange } from "./skill-range";
import type {
  UndeadUnitAbilityDefinition,
  UndeadUnitDefinition,
  UndeadUnitId,
} from "./phase-three-types";

interface UndeadUnitDocument {
  version: 1;
  units: UndeadUnitDefinition[];
}

const document = undeadUnitsJson as UndeadUnitDocument;

export const DEFAULT_UNDEAD_UNITS: UndeadUnitDefinition[] = document.units.map(
  (unit) => ({
    ...unit,
    abilities: unit.abilities.map((ability) => ({ ...ability })),
  }),
);

export const UNDEAD_UNITS: UndeadUnitDefinition[] = applyOverrides(
  DEFAULT_UNDEAD_UNITS,
);

const undeadUnitById = new Map(
  UNDEAD_UNITS.map((unit) => [unit.id, unit] as const),
);

export function getUndeadUnit(id: UndeadUnitId): UndeadUnitDefinition {
  const unit = undeadUnitById.get(id);
  if (!unit) {
    throw new Error(`Unknown undead unit: ${id}`);
  }
  return unit;
}

export function hasUndeadAbility<K extends UndeadUnitAbilityDefinition["kind"]>(
  unit: UndeadUnitDefinition,
  kind: K,
): boolean {
  return unit.abilities.some((ability) => ability.kind === kind);
}

export function validateUndeadUnitContent(): string[] {
  const errors: string[] = [];
  const ids = new Set<UndeadUnitId>();
  for (const unit of UNDEAD_UNITS) {
    if (ids.has(unit.id)) {
      errors.push(`重复亡灵单位ID: ${unit.id}`);
    }
    ids.add(unit.id);
    if (
      unit.health <= 0 ||
      unit.armor < 0 ||
      unit.damage < 0 ||
      unit.moveSpeedBaseRatio < 0 ||
      !isAllowedAttackIntervalMs(unit.intervalMs) ||
      !isAllowedSkillRange(unit.range)
    ) {
      errors.push(`亡灵单位 ${unit.id} 的基础属性无效`);
    }
    for (const ability of unit.abilities) {
      if (
        ability.kind === "cone_splash" &&
        (ability.angleDegrees <= 0 ||
          ability.angleDegrees > 360 ||
          ability.damageMultiplier < 0 ||
          !isAllowedSkillRange(ability.radius))
      ) {
        errors.push(`亡灵单位 ${unit.id} 的锥形溅射配置无效`);
      }
      if (
        ability.kind === "charge" &&
        (!isAllowedAttackIntervalMs(ability.intervalMs) ||
          !isAllowedSkillRange(ability.acquireRange) ||
          !isAllowedSkillRange(ability.pathWidth) ||
          ability.damage < 0 ||
          ability.stunDurationMs < 0)
      ) {
        errors.push(`亡灵单位 ${unit.id} 的冲锋配置无效`);
      }
      if (
        ability.kind === "periodic_area_damage" &&
        (!isAllowedAttackIntervalMs(ability.intervalMs) ||
          !isAllowedSkillRange(ability.radius) ||
          ability.damage < 0)
      ) {
        errors.push(`亡灵单位 ${unit.id} 的周期范围伤害配置无效`);
      }
      if (
        ability.kind === "kill_heal" &&
        ability.healing < 0
      ) {
        errors.push(`亡灵单位 ${unit.id} 的击杀恢复配置无效`);
      }
      if (
        ability.kind === "kill_growth" &&
        (ability.maxHealthAdd < 0 ||
          ability.currentHealthAdd < 0 ||
          ability.damageAdd < 0)
      ) {
        errors.push(`亡灵单位 ${unit.id} 的击杀成长配置无效`);
      }
    }
  }
  return [...new Set(errors)];
}

function applyOverrides(
  defaults: readonly UndeadUnitDefinition[],
): UndeadUnitDefinition[] {
  const input = workshopOverrideJson as unknown as {
    version?: number;
    sharedUnits?: { removedIds?: unknown; upserts?: unknown };
  };
  if (input.version !== 2) {
    return defaults.map((unit) => structuredClone(unit));
  }
  const removedIds = new Set(
    Array.isArray(input.sharedUnits?.removedIds)
      ? input.sharedUnits.removedIds.filter(
          (id): id is string => typeof id === "string",
        )
      : [],
  );
  const upserts = new Map(
    (Array.isArray(input.sharedUnits?.upserts)
      ? input.sharedUnits.upserts
      : [])
      .filter(
        (entry): entry is UndeadUnitDefinition =>
          Boolean(
            entry &&
              typeof entry === "object" &&
              "id" in entry &&
              typeof entry.id === "string",
          ),
      )
      .map((entry) => [entry.id, structuredClone(entry)]),
  );
  const result = defaults.flatMap((entry) =>
    removedIds.has(entry.id)
      ? []
      : [structuredClone(upserts.get(entry.id) ?? entry)],
  );
  const defaultIds = new Set(defaults.map((entry) => entry.id));
  for (const entry of upserts.values()) {
    if (!defaultIds.has(entry.id) && !removedIds.has(entry.id)) {
      result.push(structuredClone(entry));
    }
  }
  return result;
}
